/**
 * Display helper for the dual-format identifier rollout.
 *
 * Every public-facing entity now carries an optional ``uid`` (new
 * ``<PREFIX>_fgm_XXX-XXX-XXX-XXX`` form — see
 * ``apps/api/app/services/uid_generator.py``) next to whatever legacy
 * column the backend used to expose (``order_number``, ``serial_number``,
 * ``public_id``, ``slug``). The new UID is what we want to show
 * customers — anonymous, dictation-friendly — but legacy rows still
 * resolve through the old columns until the migration ages out.
 *
 * Preference order, top-down:
 *   1. ``public_number`` — the order number customers and staff see,
 *      ``xxx-xxx-xxx`` (KAN-1430 bod 4). Mirrors BE ``Order.display_number``.
 *   2. ``uid`` — present on every new row, anonymous and readable.
 *   3. ``serial_number`` — tenant-branded human-friendly fallback.
 *   4. ``order_number`` — technical fallback (legacy column).
 *   5. ``public_id`` — opaque slug for batches / projects / quotes.
 *   6. ``slug`` — opaque slug for OrderItem.
 *
 * Display only: URLs and API calls keep the technical identifier.
 *
 * Pass any shape that carries some subset of these fields; missing ones
 * are skipped. Returns ``""`` only when none of the candidates is set
 * (caller decides how to render the placeholder).
 */
export interface IdentifierLike {
  public_number?: string | null;
  uid?: string | null;
  serial_number?: string | null;
  order_number?: string | null;
  public_id?: string | null;
  slug?: string | null;
}

/**
 * Kdo je u nabídky „protistrana" — firma, jméno, nebo aspoň e-mail
 * (KAN-1792). Nabídky veřejné číslo nemají a ``CQ_fgm_…`` / ``Q-…`` je
 * technický identifikátor, který člověk číst nemá.
 */
export function quoteWho(entity: {
  customer_company?: string | null;
  customer_name?: string | null;
  customer_email?: string | null;
}): string {
  return entity.customer_company || entity.customer_name || entity.customer_email || "";
}

export function displayId(entity: IdentifierLike): string {
  return (
    entity.public_number ??
    entity.uid ??
    entity.serial_number ??
    entity.order_number ??
    entity.public_id ??
    entity.slug ??
    ""
  );
}

/**
 * Zkrácené ``000-123`` — tvar, který zákazník do KAN-2018 dostával
 * v e-mailech a PDF. Od KAN-2018 se vypisuje jen plné ``xxx-xxx-xxx``
 * (jeden formát čísla objednávky), zkrácený tvar se jen přijímá na vstupu.
 */
const SHORT_PREFIX = "000-";

/**
 * Doplní zkrácený vstup na plné veřejné číslo (KAN-1912, zrcadlo BE
 * ``normalize_public_number_input``): ``000-123`` i ``000123`` →
 * ``000-000-123``. Neplatný vstup vrací beze změny.
 */
export function normalizePublicNumberInput(raw: string): string {
  const cleaned = raw.trim();
  if (/^\d{3}-\d{3}$/.test(cleaned)) return SHORT_PREFIX + cleaned;
  if (/^\d{6}$/.test(cleaned)) {
    return `${SHORT_PREFIX}${cleaned.slice(0, 3)}-${cleaned.slice(3)}`;
  }
  return raw;
}
